"use client";

import * as React from "react";
import { Phone, MapPin, Clock, ShieldCheck, CircleCheck as CheckCircle2, CircleAlert as AlertCircle, Send, ArrowRight, Loader as Loader2 } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
interface AreaItem {
  name: string;
  county: string;
  time: string;
}
const serviceAreas: AreaItem[] = [{
  name: "Jonesboro",
  county: "Clayton County (HQ)",
  time: "15-30 min"
}, {
  name: "Morrow",
  county: "Clayton County",
  time: "20-35 min"
}, {
  name: "Riverdale",
  county: "Clayton County",
  time: "20-35 min"
}, {
  name: "Forest Park",
  county: "Clayton County",
  time: "25-40 min"
}, {
  name: "Stockbridge",
  county: "Henry County",
  time: "25-45 min"
}, {
  name: "Fayetteville",
  county: "Fayette County",
  time: "30-45 min"
}, {
  name: "Lovejoy",
  county: "Clayton County",
  time: "15-30 min"
}, {
  name: "McDonough",
  county: "Henry County",
  time: "30-50 min"
}];
const dispatchHighlights = ["Licensed & insured Clayton County plumbers", "Fully stocked mobile emergency trucks", "Upfront pricing with zero hidden trip fees", "Same-day & 24/7 rapid response dispatch"];
function DispatchRequestForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center rounded-xl bg-muted border border-border">
        <div className="size-14 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4">
          <CheckCircle2 className="size-8" />
        </div>
        <h3 className="text-xl font-bold font-default text-foreground mb-2">
          Dispatch Request Received
        </h3>
        <p className="text-muted-foreground text-sm max-w-sm mb-6">
          {successMessage || "Our on-call technician has been notified and will call you within minutes to confirm arrival time."}
        </p>
        <Button onClick={resetForm} variant="outline" size="sm">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-4">
        <FormField control={control} name="source_page" render={({
        field
      }) => <input type="hidden" {...field} value="Water Heater Problems" />} />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField control={control} name="full_name" rules={{
          required: "Full name is required",
          minLength: {
            value: 2,
            message: "Please enter your name"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground font-medium">Your Name</FormLabel>
                <FormControl>
                  <Input placeholder="e.g. Marcus Johnson" className="bg-background px-3 py-2" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="phone" rules={{
          required: "Phone number is required for dispatch",
          minLength: {
            value: 10,
            message: "Enter a valid phone number"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground font-medium">Phone Number</FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="(404) 555-0199" className="bg-background px-3 py-2" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField control={control} name="city_area" rules={{
          required: "Please specify your city or neighborhood"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground font-medium">City / Service Area</FormLabel>
                <FormControl>
                  <Input placeholder="e.g. Jonesboro, GA" className="bg-background px-3 py-2" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="urgency" rules={{
          required: "Please select an urgency level"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground font-medium">Urgency Level</FormLabel>
                <FormControl>
                  <select className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] md:text-sm text-foreground" {...field}>
                    <option value="" disabled>Select urgency level...</option>
                    <option value="Immediate Emergency">Immediate Emergency</option>
                    <option value="Within 24 Hours">Within 24 Hours</option>
                    <option value="This Week">This Week</option>
                    <option value="Just Exploring Options">Just Exploring Options</option>
                  </select>
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <FormField control={control} name="issue_details" rules={{
        required: "Please describe your water heater issue",
        minLength: {
          value: 6,
          message: "Please provide a few details"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Plumbing Issue Details</FormLabel>
              <FormControl>
                <Textarea placeholder="Describe what's leaking, clogged, or broken..." className="bg-background min-h-[96px] px-3 py-2" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="consent" rules={{
        required: "You must agree to receive service contact communications",
        validate: val => val === true || "Consent is required to submit"
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <div className="flex items-start gap-2.5">
                <FormControl>
                  <input type="checkbox" checked={Boolean(field.value)} onChange={e => field.onChange(e.target.checked)} className="size-4 rounded border-input text-primary focus:ring-ring focus:ring-2 accent-primary shrink-0 mt-0.5 cursor-pointer" />
                </FormControl>
                <FormLabel className="text-xs text-muted-foreground font-normal leading-tight cursor-pointer">
                  I consent to receive calls and messages regarding this emergency service request.
                </FormLabel>
              </div>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm flex items-center gap-2">
            <AlertCircle className="size-4 shrink-0" />
            <span>{submitError}</span>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full h-11 text-base font-semibold shadow-md bg-accent text-accent-foreground hover:bg-accent/90">
          {isSubmitting ? <>
              <Loader2 className="size-4 animate-spin mr-2" />
              Dispatching Request...
            </> : <>
              <Send className="size-4 mr-2" />
              Request Emergency Dispatch
            </>}
        </Button>

        <p className="text-xs text-center text-muted-foreground">
          For urgent flooding or gas leaks, call our 24/7 hotline directly.
        </p>
      </div>
    </Form>;
}
export default function ServiceContact() {
  const prefersReducedMotion = useReducedMotion();
  const containerAnimation = prefersReducedMotion ? {} : {
    initial: {
      opacity: 0,
      y: 16
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true
    },
    transition: {
      duration: 0.5
    }
  };
  return <section id="serviceareacontact" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-t border-border overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <Badge variant="outline" className="mb-3 px-3 py-1 font-semibold text-primary border-primary/20 bg-primary/5">
            Local Coverage & Fast Dispatch
          </Badge>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold font-default tracking-tight text-foreground">
            Service Areas & 24/7 Contact
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-sans">
            Headquartered in Jonesboro, Georgia, our emergency plumbing crews are strategically positioned across Clayton, Henry, and Fayette counties for rapid arrival.
          </p>
        </div>

        {/* Top Priority Emergency Banner */}
        <div className="mb-12 p-6 sm:p-8 rounded-2xl bg-primary text-primary-foreground shadow-lg flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-start sm:items-center gap-4">
            <div className="size-12 sm:size-14 rounded-full bg-accent text-accent-foreground flex items-center justify-center shrink-0 shadow-md">
              <Phone className="size-6 animate-pulse" />
            </div>
            <div>
              <span className="text-xs uppercase tracking-wider font-semibold text-accent">Immediate 24-Hour Assistance</span>
              <h3 className="text-xl sm:text-2xl font-bold font-default text-primary-foreground">
                Experiencing a Plumbing Emergency?
              </h3>
              <p className="text-sm text-primary-foreground/80 mt-1 max-w-xl">
                Burst pipes, sewer backups, and failing water heaters cannot wait. Call now for priority technician dispatch.
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row w-full md:w-auto items-stretch sm:items-center gap-3">
            <a href="tel:4708479076" className="inline-block w-full sm:w-auto">
              <Button size="lg" className="w-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold shadow-md">
                <Phone className="size-4 mr-2" />
                (470) 847-9076
              </Button>
            </a>
            <Link to="/services/" className="inline-block w-full sm:w-auto">
              <Button size="lg" variant="secondary" className="w-full font-medium">
                View All Services
              </Button>
            </Link>
          </div>
        </div>

        {/* Main 2-Column Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          
          {/* Left Column: Service Areas & Info */}
          <motion.div {...containerAnimation} className="lg:col-span-6 space-y-8">
            <Card className="border border-border shadow-md bg-card">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-2 text-primary font-semibold text-sm">
                  <MapPin className="size-4 text-accent" />
                  <span>Clayton & South Metro Georgia</span>
                </div>
                <CardTitle className="text-2xl font-bold font-default text-card-foreground">
                  Communities We Proudly Serve
                </CardTitle>
                <CardDescription>
                  Units on standby 24 hours a day with typical arrival times across south Atlanta suburbs:
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {serviceAreas.map((area, i) => <div key={area.name} data-index={i} className="p-3 rounded-lg border border-border bg-background flex flex-col justify-between hover:border-primary/40 transition-colors">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-foreground text-sm">{area.name}</span>
                        <span className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground font-mono">
                          {area.time}
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground mt-1">{area.county}</span>
                    </div>)}
                </div>

                <div className="pt-4 border-t border-border">
                  <h4 className="text-sm font-semibold text-card-foreground mb-3 flex items-center gap-2">
                    <ShieldCheck className="size-4 text-primary" />
                    Why Locals Trust Jonesboro Emergency Plumber:
                  </h4>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-muted-foreground">
                    {dispatchHighlights.map((item, i) => <li key={i} data-index={i} className="flex items-center gap-2">
                        <CheckCircle2 className="size-3.5 text-accent shrink-0" />
                        <span>{item}</span>
                      </li>)}
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Direct Contact Details Mini-Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-5 rounded-xl border border-border bg-muted/50 flex items-start gap-3">
                <Clock className="size-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-bold text-foreground font-default">Hours of Operation</h4>
                  <p className="text-xs text-muted-foreground mt-1">24 Hours a Day / 7 Days a Week</p>
                  <p className="text-xs text-muted-foreground">365 Days a Year (Including Holidays)</p>
                </div>
              </div>

              <div className="p-5 rounded-xl border border-border bg-muted/50 flex items-start gap-3">
                <MapPin className="size-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-bold text-foreground font-default">Main Dispatch Center</h4>
                  <p className="text-xs text-muted-foreground mt-1">Jonesboro, GA 30236</p>
                  <p className="text-xs text-muted-foreground">Serving within a 35-mile radius</p>
                </div>
              </div>
            </div>

            <div className="p-4 rounded-xl border border-dashed border-border bg-card flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                Need to explore specific repair services before requesting service?
              </span>
              <Link to="/service-areas/">
                <Button variant="ghost" size="sm" className="text-primary font-semibold text-xs">
                  Area Details <ArrowRight className="size-3 ml-1" />
                </Button>
              </Link>
            </div>
          </motion.div>

          {/* Right Column: Dispatch & Contact Request Form */}
          <motion.div {...containerAnimation} className="lg:col-span-6">
            <Card className="border border-border shadow-xl bg-card">
              <CardHeader className="pb-4 border-b border-border">
                <div className="flex items-center justify-between">
                  <Badge variant="secondary" className="font-semibold text-xs">
                    Online Service Request
                  </Badge>
                  <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <span className="size-2 rounded-full bg-accent animate-ping" />
                    Live Technicians On-Duty
                  </span>
                </div>
                <CardTitle className="text-2xl font-bold font-default text-card-foreground mt-2">
                  Send a Service Request
                </CardTitle>
                <CardDescription>
                  Fill in your details below for swift dispatch or schedule a routine inspection with our local technicians.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <FormProvider formId="water_heater_service_form" sectionName="water-heater-service-contact" defaultValues={{
                full_name: "",
                phone: "",
                city_area: "",
                urgency: "",
                issue_details: "",
                consent: false,
                source_page: "Water Heater Problems"
              }} validationRules={{
                full_name: {
                  required: true
                },
                phone: {
                  required: true
                },
                city_area: {
                  required: true
                },
                urgency: {
                  required: true
                },
                issue_details: {
                  required: true
                },
                consent: {
                  required: true
                }
              }} submitText="Request Emergency Dispatch" successMessage="Thank you! Our dispatch team has received your service request and will call you shortly.">
                  <DispatchRequestForm />
                </FormProvider>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}